import { AccessTree, type CompanyOption } from "@intility/access-tree";
import Button from "@intility/bifrost-react/Button";
import Input from "@intility/bifrost-react/Input";
import Section from "@intility/bifrost-react/Section";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCompanies } from "~/api/catalog";
import { deleteTuples, writeTuples } from "~/api/client";
import { useCapabilities } from "~/auth/capabilities";
import { auth } from "~/auth/index";
import { PageLayout } from "~/components/layout/PageLayout";

const FGA_API_BASE = (import.meta.env.VITE_FGA_API_URL || "/fga-api").replace(
  /\/api$/,
  "",
);

const INTILITY_ID = "fe05eb0b-5760-44c7-9ade-b68d523d4999";

const QUICK_SCOPES = [
  { label: "Provider", scopeRoot: "provider:intility" },
  { label: "Organization", scopeRoot: "organization:intility-as" },
  { label: "Company", scopeRoot: `company:${INTILITY_ID}` },
  {
    label: "Resource",
    scopeRoot: `resource:${INTILITY_ID}/id-ticket-manager-demo/tickets`,
  },
];

async function getToken(): Promise<string> {
  return auth.getToken();
}

function useRemainingHeight(ref: React.RefObject<HTMLElement | null>) {
  const [height, setHeight] = useState<number>(600);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setHeight(Math.max(200, window.innerHeight - rect.top - 16));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [ref]);

  return height;
}

export default function AccessTreePage() {
  const { can } = useCapabilities();
  const [inputValue, setInputValue] = useState("");
  const [scopeRoot, setScopeRoot] = useState<string | null>(null);
  const [platformMode, setPlatformMode] = useState<string | null>(null);
  const treeRef = useRef<HTMLDivElement>(null);
  const treeHeight = useRemainingHeight(treeRef);

  const { data: catalogCompanies } = useCompanies();
  const companyOptions = useMemo<CompanyOption[] | undefined>(
    () =>
      catalogCompanies?.map((c) => ({ id: c.id, name: c.displayName ?? c.id })),
    [catalogCompanies],
  );

  const writeTuple = useCallback(
    async (tuple: { user: string; relation: string; object: string }) => {
      await writeTuples({ writes: [tuple] });
    },
    [],
  );

  const deleteTuple = useCallback(
    async (tuple: { user: string; relation: string; object: string }) => {
      await deleteTuples([tuple]);
    },
    [],
  );

  function loadScopeRoot(value: string) {
    setPlatformMode(null);
    setScopeRoot(value || null);
  }

  function loadPlatform(slug: string) {
    setScopeRoot(null);
    setPlatformMode(slug);
  }

  function clearScope() {
    setInputValue("");
    setScopeRoot(null);
    setPlatformMode(null);
  }

  return (
    <PageLayout
      title="Access Tree"
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Access Tree" }]}
      description="View the object hierarchy and grants. Switch to another user to inspect or edit their access."
      fullWidthBody
    >
      <Section>
        <Section.Content>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <div style={{ width: 320, flexShrink: 0 }}>
              <Input
                label="Object root"
                placeholder="provider:intility"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && loadScopeRoot(inputValue.trim())
                }
              />
            </div>
            <Button
              onClick={() => loadScopeRoot(inputValue.trim())}
              variant="filled"
              disabled={!inputValue.trim()}
            >
              Load
            </Button>
            {QUICK_SCOPES.map((qs) => (
              <Button
                key={qs.scopeRoot}
                variant="basic"
                onClick={() => {
                  setInputValue(qs.scopeRoot);
                  loadScopeRoot(qs.scopeRoot);
                }}
              >
                {qs.label}
              </Button>
            ))}
            <Button
              variant="basic"
              onClick={() => loadPlatform("id-ticket-manager-demo")}
            >
              Platform
            </Button>
            {(scopeRoot || platformMode) && (
              <Button variant="flat" onClick={clearScope}>
                Clear
              </Button>
            )}
          </div>
        </Section.Content>
      </Section>

      <Section>
        <Section.Content>
          <div ref={treeRef}>
            <AccessTree
              queryApiBaseUrl={FGA_API_BASE}
              scopeRoot={scopeRoot}
              platform={platformMode ?? undefined}
              companies={platformMode ? companyOptions : undefined}
              getToken={getToken}
              showUserPicker
              height={treeHeight}
              viewerDisplayName={
                auth.keycloak.tokenParsed?.name ??
                auth.keycloak.tokenParsed?.preferred_username ??
                "Yourself"
              }
              viewerSubject={
                auth.keycloak.tokenParsed?.sub
                  ? `user:${auth.keycloak.tokenParsed.sub}`
                  : undefined
              }
              editing={
                can("authorization.write")
                  ? { writeTuple, deleteTuple }
                  : undefined
              }
            />
          </div>
        </Section.Content>
      </Section>
    </PageLayout>
  );
}
